<template>
  <div class="class-1">
    <!-- Header -->
    <app-header />

    <!-- Language Form -->
    <div class="container mt-4">
      <div class="row">
        <div class="col-12">
          <h2>Edit Language</h2>
          <form @submit.prevent="submitForm">
            <!-- Full Name -->
            <div class="form-row">
              <div class="form-group col-md-12">
                <label for="fullName">Full Name</label>
                <div class="searchbyphone d-flex flex-row">
                  <div class="infobox fieldOuter w-100">
                    <input
                      type="text"
                      class="form-control"
                      id="fullName"
                      placeholder="Enter full language name"
                      v-model="form.fullName"
                      ref="fullName"
                      required
                    >
                  </div>
                  <span class="icon1 icon2" @click="openKeypad('fullName')">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512"></svg>
                  </span>
                </div>
              </div>
            </div>

            <div class="form-row">
              <div class="form-group col-md-12">
                <label for="sequence">Sequence</label>
                <div class="searchbyphone d-flex flex-row">
                  <div class="infobox fieldOuter w-100">
                    <input
                      type="number"
                      class="form-control"
                      id="sequence"
                      placeholder="Enter sequence (e.g., 2, 3)"
                      v-model="form.sequence"
                      ref="sequence"
                      required
                      maxlength="2"
                    >
                  </div>
                  <span class="icon1 icon2" @click="openKeypad('sequence')">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512">
                      <!-- Keyboard icon SVG -->
                    </svg>
                  </span>
                </div>
              </div>
            </div>

            <!-- Short Name (readonly) -->
            <div class="form-row">
              <div class="form-group col-md-12">
                <label for="shortName">Short Code</label>
                <input
                  type="text"
                  class="form-control"
                  id="shortName"
                  v-model="form.shortName"
                  readonly
                >
              </div>
            </div>

            <!-- Direction -->
            <div class="form-row">
              <div class="form-group col-md-12">
                <label for="direction">Text Direction</label>
                <select
                  class="form-control"
                  id="direction"
                  v-model="form.direction"
                  required
                >
                  <option value="1">Right-to-Left (RTL)</option>
                  <option value="0">Left-to-Right (LTR)</option>
                </select>
              </div>
            </div>

            <!-- Download Template -->
            <div class="form-row">
              <div class="form-group col-md-12">
                <button 
                  type="button" 
                  class="btn btn-template"
                  @click="downloadTemplate"
                >
                  <i class="fas fa-download"></i> Download Translation Template
                </button>
                <small class="form-text text-muted">
                  Download the template file to prepare your translations
                </small>
              </div>
            </div>

            <!-- Language File Update -->
            <div class="form-row">
              <div class="form-group col-md-12">
                <label for="languageFile">Update Language File (JSON)</label>
                <input
                  type="file"
                  class="form-control-file"
                  id="languageFile"
                  @change="handleFileUpload"
                  accept=".json"
                >
                <small class="text-muted">Current: {{ form.links }}</small>
              </div>
            </div>

            <!-- Default Language -->
            <div class="form-row">
              <div class="form-group col-md-12">
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    id="isDefault"
                    v-model="form.isDefault"
                  >
                  <label class="form-check-label" for="isDefault">Set as default language</label>
                </div>
              </div>
            </div>

            <!-- Status -->
            <div class="form-row">
              <div class="form-group col-md-12">
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    id="isActive"
                    v-model="form.isActive"
                  >
                  <label class="form-check-label" for="isActive">Active Language</label>
                </div>
              </div>
            </div>

            <!-- Buttons -->
            <div class="form-row">
              <div class="form-group col-12 d-flex justify-content-between">
                <button type="button" class="btn btn-secondary" @click="cancelForm">Cancel</button>
                <button type="submit" class="btn btn-primary">Update Language</button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>

    <Keypad 
      ref="keypad"
      :target-input="currentInputRef"
      @update:value="updateInputValue"
      @cancel="closeKeypad"
    />
  </div>
</template>

<script>
import Keypad from './keypad_popup_component.vue';
import { Modal } from 'bootstrap';

export default {
  components: {
    Keypad
  },
  
  data() {
    return {
      form: {
        id: null,
        fullName: '',
        sequence: '',
        shortName: '',
        direction: '0',
        links: '',
        isDefault: false,
        isActive: true,
        branchId: localStorage.getItem('branchId')
      },
      currentInputRef: null,
      currentInputName: null,
      keypadModal: null
    };
  },
  created() {
    this.fetchLanguageData();
  },
  mounted() {
    this.$nextTick(() => {
      const modalElement = this.$refs.keypad.$el;
      this.keypadModal = new Modal(modalElement);
    });
  },
  methods: {
    async downloadTemplate() {
      try {
        this.$root.isLoading = true;
        const token = localStorage.getItem('authToken');
        const url = `${this.$BASE_API_URL}/files/translation_file.xlsx`;
        
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to download template');
        }

        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', 'translation_template.xlsx');
        document.body.appendChild(link);
        link.click();
        
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
        
        this.$toast.success('Template downloaded successfully');
      } catch (error) {
        console.error('Error downloading template:', error);
        this.$toast.error('Failed to download template');
      } finally {
        this.$root.isLoading = false;
      }
    },
    async fetchLanguageData() {
      try {
        this.$root.isLoading = true;
        const token = localStorage.getItem('authToken');
        const languageId = this.$route.params.id;
        
        const response = await fetch(`${this.$BASE_API_URL}/api/languages/${languageId}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch language data');
        }

        const { data } = await response.json();
        this.form = {
          id: data.id,
          fullName: data.fullName,
          shortName: data.shortName,
          sequence: data.sequence,
          direction: data.direction.toString(),
          links: data.links,
          isDefault: data.isDefault,
          isActive: !data.isSuspend,
          branchId: localStorage.getItem('branchId')
        };
      } catch (error) {
        console.error('Error fetching language:', error);
        this.$toast.error('Failed to load language data');
        this.$router.go(-1);
      } finally {
        this.$root.isLoading = false;
      }
    },
    async submitForm() {
      try {
        this.$root.isLoading = true;
        const token = localStorage.getItem('authToken');
        
        const payload = {
          shortName: this.form.shortName,
          fullName: this.form.fullName,
          direction: this.form.direction,
          links: this.form.links || 'default_link',
          isDefault: this.form.isDefault ? 1 : 0,
          isSuspend: this.form.isActive ? 0 : 1,
          branchId: this.form.branchId,
          sequence: this.form.sequence,
        };

        const response = await fetch(`${this.$BASE_API_URL}/api/languages/${this.form.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });

        const responseData = await response.json();
        if (response.ok && responseData.status) {
          this.$toast.success(responseData.message || 'Language updated successfully!');
          this.$router.go(-1);
        } else {
          throw new Error(responseData.message || 'Failed to update language');
        }
      } catch (error) {
        console.error('Error updating language:', error);
        this.$toast.error(error.message || 'An error occurred. Please try again.');
      } finally {
        this.$root.isLoading = false;
      }
    },
    handleFileUpload(event) {
      const file = event.target.files[0];
      if (file && file.type === 'application/json') {
        this.form.languageFile = file;
      } else {
        this.$toast.error('Please upload a valid JSON file');
        event.target.value = '';
      }
    },
    cancelForm() {
      this.$router.go(-1);
    },
    openKeypad(inputRef) {
      this.currentInputRef = this.$refs[inputRef];
      this.currentInputName = inputRef;
      this.keypadModal.show();
    },
    updateInputValue(value) {
      if (this.currentInputRef) {
        this.currentInputRef.value = value;
        this.form[this.currentInputName] = value;
      }
    },
    closeKeypad() {
      if (this.keypadModal) {
        this.keypadModal.hide();
      }
      this.removeBackdrop();
      this.currentInputRef = null;
      this.currentInputName = null;
    },
    removeBackdrop() {
      const backdrops = document.querySelectorAll('.modal-backdrop');
      backdrops.forEach(backdrop => backdrop.remove());
      document.body.classList.remove('modal-open');
      document.body.style.paddingRight = '';
    }
  }
};
</script>

<style scoped>
.searchbyphone {
  position: relative;
}

.infobox.fieldOuter {
  position: relative;
  width: 100%;
}

.icon1.icon2 {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 38px;
  background: #f8f9fa;
  border: 1px solid #ced4da;
  border-left: none;
  cursor: pointer;
}

.icon1.icon2 svg {
  width: 20px;
  height: 20px;
  fill: #495057;
}

.form-control {
  border-radius: 0.25rem 0 0 0.25rem !important;
}

.class-1 {
  font-family: 'Poppins', sans-serif;
}

.btn-primary {
  background-color: #4caf50;
  border-color: #4caf50;
}

.btn-primary:hover {
  background-color: #3e8e41;
  border-color: #3e8e41;
}

.btn-template {
  background-color: #17a2b8;
  color: white;
  padding: 8px 16px;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  transition: background-color 0.3s;
}

.btn-template:hover {
  background-color: #138496;
}

.btn-template i {
  margin-right: 8px;
}

.form-text.text-muted {
  display: block;
  margin-top: 5px;
  font-size: 0.875em;
  color: #6c757d;
}

.text-muted {
  color: #6c757d;
  font-size: 0.875em;
  margin-top: 5px;
}
</style>